import { useState } from "react";
import { Moon, Sun, LogOut } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import BottomNav from "./BottomNav";
import NotificationBell from "./NotificationBell";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useThemeStore } from "@/stores/useThemeStore";
import {
  useAppStore,
  useCurrentMonth,
} from "@/features/monthly-summary/store/useAppStore";
import { useAuthStore } from "@/stores/useAuthStore";
import { useAccountStore } from "@/features/accounts/store/useAccountStore";
import { logoutRequest } from "@/services/authApi";
import { dayjs } from "@boo/shared";
import { useFeatureFlagStore } from "@/features/feature-flags/store/useFeatureFlagStore";
import { FeatureFlag } from "@/features/feature-flags/types/featureFlags.types";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function MonthPicker({
  viewYear,
  viewMonth,
  onSelect,
  onClose,
}: {
  viewYear: number;
  viewMonth: number;
  onSelect: (year: number, month: number) => void;
  onClose: () => void;
}) {
  const [pickerYear, setPickerYear] = useState(viewYear);
  const now = dayjs();
  const nowYear = now.year();
  const nowMonth = now.month() + 1;

  return (
    <div className="p-1">
      {/* Year nav */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={() => setPickerYear((y) => y - 1)}
          className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/10 text-muted-foreground"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-foreground">
          {pickerYear}
        </span>
        <button
          onClick={() => setPickerYear((y) => y + 1)}
          disabled={pickerYear >= nowYear}
          className={`w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground ${pickerYear >= nowYear ? "opacity-30 cursor-not-allowed" : "hover:bg-black/5 dark:hover:bg-white/10"}`}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
      {/* Month grid */}
      <div className="grid grid-cols-3 gap-1">
        {MONTHS.map((label, i) => {
          const m = i + 1;
          const isFuture =
            pickerYear > nowYear || (pickerYear === nowYear && m > nowMonth);
          const isSelected = pickerYear === viewYear && m === viewMonth;
          return (
            <button
              key={m}
              disabled={isFuture}
              onClick={() => {
                onSelect(pickerYear, m);
                onClose();
              }}
              className={`rounded-lg py-1.5 text-[13px] font-medium transition-colors ${
                isFuture
                  ? "opacity-30 cursor-not-allowed text-muted-foreground"
                  : isSelected
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-black/5 dark:hover:bg-white/10 text-foreground"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GhostLogo() {
  return (
    <img src="/app_icon.svg" width="36" height="36" alt="" aria-hidden="true" />
  );
}

function MonthChip() {
  const [open, setOpen] = useState(false);
  const { viewYear, viewMonth, loadMonth, goToPrevMonth, goToNextMonth } =
    useAppStore();
  const currentMonth = useCurrentMonth();
  const now = dayjs();
  const isLatestMonth =
    viewYear === now.year() && viewMonth === now.month() + 1;

  return (
    <div
      className="inline-flex items-center gap-0 sm:gap-2 rounded-full px-1 sm:px-3.5 py-1.5 text-[13px] sm:text-sm font-medium whitespace-nowrap min-w-0 shadow-card bg-card border-[0.5px] border-border"
      aria-label="Month selector"
    >
      <button
        onClick={goToPrevMonth}
        className="w-[26px] h-[26px] rounded-full flex items-center justify-center transition-colors hover:bg-black/5 dark:hover:bg-white/10 text-[var(--text-secondary)]"
        aria-label="Previous month"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="px-1 text-foreground hover:text-foreground/70 transition-colors sm:min-w-[110px] text-center whitespace-nowrap">
            <span className="sm:hidden">
              {MONTHS[viewMonth - 1]} {viewYear}
            </span>
            <span className="hidden sm:inline">
              {currentMonth?.month ?? "—"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3" align="center">
          <MonthPicker
            viewYear={viewYear}
            viewMonth={viewMonth}
            onSelect={loadMonth}
            onClose={() => setOpen(false)}
          />
        </PopoverContent>
      </Popover>
      <button
        className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-[var(--text-secondary)] transition-colors ${isLatestMonth ? "opacity-30 cursor-not-allowed" : "hover:bg-black/5 dark:hover:bg-white/10"}`}
        aria-label="Next month"
        onClick={isLatestMonth ? undefined : goToNextMonth}
        disabled={isLatestMonth}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
}

function TopControls() {
  const [confirmLogout, setConfirmLogout] = useState(false);
  const { isDark, toggle } = useThemeStore();
  const clearToken = useAuthStore((s) => s.clearToken);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logoutRequest();
    } finally {
      clearToken();
      navigate("/login");
    }
  };

  return (
    <div className="flex items-center gap-0 sm:gap-3 shrink-0">
      <NotificationBell />
      <div className="hidden sm:flex items-center gap-2">
        {isDark ? (
          <Moon className="w-4 h-4 text-muted-foreground" />
        ) : (
          <Sun className="w-4 h-4 text-muted-foreground" />
        )}
        <Switch
          checked={isDark}
          onCheckedChange={toggle}
          aria-label="Toggle dark mode"
        />
      </div>
      {/* Mobile: single icon button keeps the header on one row */}
      <button
        onClick={toggle}
        className="sm:hidden w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label="Toggle dark mode"
        aria-pressed={isDark}
      >
        {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
      </button>
      <Popover open={confirmLogout} onOpenChange={setConfirmLogout}>
        <PopoverTrigger asChild>
          <button
            className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-44 p-3" align="end">
          <p className="text-sm font-medium text-foreground mb-3">Log out?</p>
          <div className="flex gap-2">
            <button
              onClick={handleLogout}
              className="flex-1 text-xs font-medium text-white bg-red-500 hover:bg-red-600 px-3 py-1.5 rounded-lg transition-colors"
            >
              Log out
            </button>
            <button
              onClick={() => setConfirmLogout(false)}
              className="flex-1 text-xs text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              Cancel
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function TopStrip() {
  const { pathname } = useLocation();
  const suggestionCount = useAccountStore((s) => s.suggestions.length);
  const flags = useFeatureFlagStore((s) => s.flags);

  return (
    <>
      <div className="mb-5 sm:mb-7 space-y-3">
        {/* Desktop: 3-column grid so month chip is truly centered */}
        <div className="hidden sm:grid sm:grid-cols-3 sm:items-center sm:[&>*:first-child]:self-start">
          {/* Left: brand + nav stacked */}
          <div className="flex flex-col gap-4">
            <Link to="/" className="flex items-center gap-2.5 w-fit">
              <div className="flex-shrink-0">
                <GhostLogo />
              </div>
              <div className="font-bold text-[15px] leading-tight text-foreground">
                BOO
              </div>
            </Link>
            <div className="flex items-center gap-1 pl-1">
              {[
                { to: "/accounts", label: "Accounts" },
                { to: "/", label: "Summary" },
                { to: "/budget", label: "Budget", flag: FeatureFlag.Budget },
                { to: "/bills", label: "Bills" },
                { to: "/friends", label: "Friends" },
                {
                  to: "/recurring-expenses",
                  label: "Recurring",
                  flag: FeatureFlag.Recurring,
                },
                {
                  to: "/tax-planner",
                  label: "Tax Planner",
                  flag: FeatureFlag.TaxPlanner,
                },
                { to: "/settings", label: "Settings" },
              ]
                .filter(({ flag }) => !flag || flags[flag])
                .map(({ to, label }) => (
                  <Link
                    key={to}
                    to={to}
                    className={`text-[12px] transition-colors px-2 py-0.5 rounded-md ${
                      pathname === to
                        ? "text-foreground bg-muted font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    {label}
                    {to === "/accounts" && suggestionCount > 0 && (
                      <span
                        aria-label={`${suggestionCount} account suggestions`}
                        className="ml-1 inline-flex min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-medium leading-4 text-white"
                      >
                        {suggestionCount}
                      </span>
                    )}
                  </Link>
                ))}
            </div>
          </div>
          {/* Center: month chip (summary only) */}
          <div className="flex justify-center">
            {pathname === "/" && <MonthChip />}
          </div>
          {/* Right: controls */}
          <div className="flex justify-end">
            <TopControls />
          </div>
        </div>

        {/* Mobile layout: one row. On summary the month chip takes the
            wordmark's place. */}
        <div className="sm:hidden flex items-center justify-between gap-1">
          {/* Brand */}
          <Link
            to="/"
            className="flex items-center gap-2.5 shrink-0"
            aria-label="BOO"
          >
            <div className="flex-shrink-0">
              <GhostLogo />
            </div>
            {pathname !== "/" && (
              <div className="font-bold text-[15px] leading-tight text-foreground">
                BOO
              </div>
            )}
          </Link>
          {pathname === "/" && <MonthChip />}
          <TopControls />
        </div>
      </div>
      <BottomNav />
    </>
  );
}
